/**
 * Version/Revision API functions
 */

import { apiClient } from '../../../api/client'
import LogTool from '../../../logger/logTools'
import { type Version, type VersionComparison } from '../utils/types'

const log = new LogTool({ context: 'VersionAPI', enable: true, logLevel: 'warn' })

/**
 * Helper to ensure URL has trailing slash
 */
function ensureTrailingSlash(value: string): string {
  if (!value) {
    throw new Error('Object self link is required to perform version operations')
  }
  return value.endsWith('/') ? value : `${value}/`
}

/**
 * Build version-related URL from object self link
 */
function buildVersionsUrl(objectSelf: string, suffix: string): string {
  const base = ensureTrailingSlash(objectSelf)
  return `${base}${suffix}`
}

/**
 * Fetch version history for an object
 */
export async function fetchVersionHistory(objectSelf: string): Promise<Version[]> {
  log.info('Fetching version history for:', objectSelf)

  const url = buildVersionsUrl(objectSelf, 'versions/')
  const response = await apiClient.get<any>(url)

  // Support both formats: array directly or paginated format with results
  if (Array.isArray(response)) {
    return response as Version[]
  }
  
  if (response && typeof response === 'object' && 'results' in response && Array.isArray(response.results)) {
    return response.results as Version[]
  }

  const shapeError = new Error('Unexpected version history response shape')
  log.error(shapeError, response)
  throw shapeError
}

/**
 * Fetch comparison between two versions
 */
export async function fetchVersionComparison(
  objectSelf: string,
  version1Id: string,
  version2Id: string
): Promise<VersionComparison> {
  log.info('Fetching version comparison for:', objectSelf, version1Id, version2Id)

  const url = buildVersionsUrl(objectSelf, 'compare_versions/')
  const payload = {
    version1_id: version1Id,
    version2_id: version2Id,
  }
  
  const response = await apiClient.post<any>(url, payload)

  if (!response || !Array.isArray((response as VersionComparison).compare_data)) {
    const shapeError = new Error('Unexpected version comparison response shape')
    log.error(shapeError, response)
    throw shapeError
  }

  return response as VersionComparison
}

/**
 * Revert object to a specific version
 */
export async function revertObjectToVersion(
  objectSelf: string,
  version: Version
): Promise<string> {
  log.info('Reverting to version:', version.version_id, 'for object:', objectSelf)

  const url = buildVersionsUrl(objectSelf, 'revert/')
  const payload = { version }
  
  const response = await apiClient.patch<any>(url, payload)

  // Backend now returns the reverted object as JSON instead of a text message
  if (typeof response === 'object' && response !== null) {
    return `Reverted to version ${version.version_id}`
  }

  if (typeof response === 'string') {
    return response
  }

  return JSON.stringify(response)
}

/**
 * Revert to a specific version (alternative function)
 */
export async function revertToVersionApi(
  resourceUrl: string,
  versionId: string | number
): Promise<any> {
  log.info('Reverting to version:', versionId, 'for resource:', resourceUrl)

  const revertUrl = resourceUrl.endsWith('/') 
    ? `${resourceUrl}v1/revert/${versionId}/` 
    : `${resourceUrl}/v1/revert/${versionId}/`
    
  const response = await apiClient.post<any>(`v1/${revertUrl}`, {})
  return response
}

